/* Styles */
import '../../styles/Testimonial/testimonials.scss';
import '../../styles/Testimonial/testimonialSubmit.scss';

/* Libraries */
import { useLocation, useNavigate } from 'react-router-dom';
import { Col, Container, Row } from 'react-bootstrap';
import React, { useEffect, useState, useCallback, useRef } from 'react';
import _ from 'lodash';

/* Components */
import { TestiHeader } from './TestimonialHeader/testiHeader';
import { TestiPhotos } from './TestimonialPhotos/testiPhotos';
import { TestiOrders } from './TestimonialOrders/testiOrders';
import Modal from '../UI/Modal/Modal'; // Updated import to use our unified Modal component
import LoadingComponent from '../UI/General/LoadingComponent';

/* Controllers & Helpers */
import { dataTestimonial } from '../../controllers/dataTestimonial';
import { inputValidator, formValidator } from '../../helpers/inputValidator';
import { getOrdersID } from '../../helpers/getDataOrders';
import { signIn } from '../../controllers/basicAuth';
import { decodeData } from '../../helpers/encoderDecoder';
import useRecaptcha from '../../hooks/useRecaptcha';
import { TestiVendor } from './TestimonialVendor/testiVendor';
import { TestiEvent } from './TestimonialEvent/testiEvent';
import TermsAndConditions from './TestimonialConditions/testiTerms';

const SHARE_WEDDING_FLAIR_URL = '/pages/share-your-wedding-flair';

export const TestimonialSubmit = () => {
  const verifyRecaptcha = useRecaptcha('testimonial_submit');
  const location = useLocation();
  const navigate = useNavigate();
  const modalTimeoutRef = useRef(null);

  // State management
  const [decodedState, setDecodedState] = useState({
    internalState: '',
    externalState: '',
    emailState: '',
    tokenState: '',
  });

  const [modalState, setModalState] = useState({
    show: false,
    headMsg: '',
    bodyMsg: '',
  });

  const [token, setToken] = useState('');
  const [successModal, setSuccessModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [orderData, setOrderData] = useState({ data: [] });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Redirect to wedding flair page if parameters are missing
  const redirectToWeddingFlair = useCallback(() => {
    navigate(SHARE_WEDDING_FLAIR_URL);
  }, [navigate]);

  // Parse URL parameters
  useEffect(() => {
    const decodeSearchParams = async () => {
      if (!location.search) {
        redirectToWeddingFlair();
        return;
      }

      const searchParams = new URLSearchParams(location.search);

      try {
        const internalState = await decodeData(searchParams.get('intState'));
        const externalState = await decodeData(searchParams.get('extState'));
        const emailState = await decodeData(searchParams.get('emaState'));
        const tokenState = await decodeData(searchParams.get('tkmState'));

        setDecodedState({
          internalState,
          externalState,
          emailState,
          tokenState,
        });
      } catch (error) {
        console.error('Error decoding search parameters:', error);
        redirectToWeddingFlair();
      }
    };

    decodeSearchParams();
  }, [location, redirectToWeddingFlair]);

  // Get authentication token
  const getAuthToken = useCallback(async () => {
    try {
      const newToken = await signIn();
      setToken(newToken);
      return newToken;
    } catch (error) {
      console.error('Authentication error:', error);
      return '';
    }
  }, []);

  // Fetch order data
  useEffect(() => {
    const fetchOrderData = async () => {
      const { internalState, tokenState } = decodedState;

      if (!internalState) return;

      setLoading(true);

      try {
        // If no token exists, get a new one
        if (!tokenState) {
          const newToken = await getAuthToken();
          if (!newToken) {
            throw new Error('Failed to obtain authentication token');
          }
        } else {
          // Fetch order data with existing token
          const orderData = await getOrdersID(internalState, tokenState);
          setOrderData({ data: orderData });
        }
      } catch (error) {
        console.error('Error fetching order data:', error);
        redirectToWeddingFlair();
      } finally {
        setLoading(false);
      }
    };

    fetchOrderData();
  }, [decodedState, getAuthToken, redirectToWeddingFlair]);

  // Display modal with message
  const showModal = useCallback((isSuccess, heading, message) => {
    // Clear any existing timeout to prevent race conditions
    if (modalTimeoutRef.current) {
      clearTimeout(modalTimeoutRef.current);
      modalTimeoutRef.current = null;
    }

    // Force modal state to update completely
    setModalState({
      show: true,
      headMsg: isSuccess ? 'Submitted' : heading,
      bodyMsg: isSuccess ? `Your Story has been submitted Successfully! <br/>${message}` : message,
    });

    // Only auto-close error modals after delay if it's not a validation error
    if (!isSuccess && !heading.includes('Required Fields')) {
      modalTimeoutRef.current = setTimeout(() => {
        setModalState(prev => ({ ...prev, show: false }));
        modalTimeoutRef.current = null;
      }, 3000);
    }
  }, []);

  // Handle modal close
  const handleModalClose = useCallback(() => {
    // When manually closing, clear any existing auto-close timeout
    if (modalTimeoutRef.current) {
      clearTimeout(modalTimeoutRef.current);
      modalTimeoutRef.current = null;
    }

    // Completely reset modal state
    setModalState({
      show: false,
      headMsg: '',
      bodyMsg: '',
    });
  }, []);

  // Verify reCAPTCHA
  const verifyUserRecaptcha = async () => {
    try {
      const recaptchaToken = await verifyRecaptcha();

      if (!recaptchaToken) {
        console.error('reCAPTCHA token not received');
        return false;
      }

      const response = await fetch(`${process.env.REACT_APP_LBDA}verifyRecaptcha`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: recaptchaToken }),
      });

      const { data } = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'reCAPTCHA verification failed');
      }

      return data.success && data.score > 0.5;
    } catch (error) {
      console.error('reCAPTCHA verification error:', error);
      return false;
    }
  };

  // Process form data
  const processFormData = formElements => {
    // Create form data object for validation
    const formData = {};

    // Collect all form values
    for (let i = 0; i < formElements.length; i++) {
      const element = formElements[i];
      const { name, value, checked, type } = element;

      if (!name) continue;

      if (type === 'checkbox') {
        formData[name] = checked;
      } else {
        formData[name] = value;
      }
    }

    // Perform complete form validation
    const validationResult = formValidator(formData);

    // If validation fails, show the modal and return null
    if (validationResult.error) {
      showModal(false, validationResult.headMsg, validationResult.bodyMsg);
      return null;
    }

    // Process products data
    const productsData = [];
    const products = orderData.data.products || [];

    products.forEach(product => {
      const productId = product.id;

      // Get product usage from form data
      const productUsedValue = formData[`proUsed_${productId}`];
      const productUsed = productUsedValue
        ? productUsedValue.split(',').map(item => parseInt(item, 10))
        : [6]; // Default value

      // Get review text and rating
      const productReview = formData[`textreview_${productId}`] || '...';
      const productRate = parseInt(formData[`review_${productId}`] || 0, 10);

      productsData.push({
        productId,
        productRate,
        productUsed,
        productReview,
      });
    });

    // Prepare final data object
    const finalData = {
      images: {},
      videos: { 0: '' },
      products: productsData,
    };

    // Process remaining form fields
    Object.entries(formData).forEach(([name, value]) => {
      // Skip certain field types
      if (name.includes('proUsed') || name.includes('review') || name.includes('textreview')) {
        return;
      }

      // Handle special fields
      switch (name) {
        case 'imageArray':
          const imageArray = value.split(',');
          imageArray.forEach((image, index) => {
            if (image) finalData.images[index] = image;
          });
          break;

        case 'videoArray':
          if (value && value.trim() !== '') {
            const videoArray = value.split(',');
            videoArray.forEach((video, index) => {
              if (video) finalData.videos[index] = video;
            });
          }
          break;

        case 'eventDate':
          try {
            finalData[name] = new Date(value).toISOString();
          } catch (e) {
            finalData[name] = new Date().toISOString();
          }
          break;

        case 'termsAgreement':
          // Handle checkbox value
          finalData[name] = value === true || value === 'true';
          break;

        default:
          // Only include non-empty values
          if (value && typeof value === 'string' && value.trim() !== '') {
            finalData[name] = value;
          } else if (value !== '' && typeof value !== 'string') {
            finalData[name] = value;
          }
          break;
      }
    });

    return finalData;
  };

  // Handle form submission
  const handleSubmit = async e => {
    e.preventDefault();

    // Set submitting state to true at the beginning
    setIsSubmitting(true);

    try {
      // Check if the submit button was clicked
      const submitter = e.nativeEvent.submitter;
      if (submitter && submitter.name !== 'submitBtn') {
        setIsSubmitting(false);
        return;
      }

      // Verify reCAPTCHA
      const isHuman = await verifyUserRecaptcha();

      if (!isHuman) {
        showModal(
          false,
          'Recaptcha Error',
          'Are you a Robot? Please reload the page and try again.'
        );
        setIsSubmitting(false);
        return;
      }

      // Process form data
      const formData = processFormData(e.target.elements);

      if (!formData) {
        setIsSubmitting(false);
        return; // Validation failed
      }

      // Get or refresh token
      const currentToken = token || (await getAuthToken());

      if (!currentToken) {
        showModal(false, 'Authentication Error', 'Failed to authenticate. Please try again.');
        setIsSubmitting(false);
        return;
      }

      // Submit testimonial
      const response = await dataTestimonial(formData, currentToken);
      setSuccessModal(true);
    } catch (error) {
      console.error('Testimonial submission error:', error);

      showModal(
        false,
        'Error Found',
        'Something went wrong while submitting your story. Please try again.'
      );
    } finally {
      // Always set submitting to false when the operation completes, whether success or error
      setIsSubmitting(false);
    }
  };

  // Handle individual field validation
  const handleFieldValidation = e => {
    const { name, value, checked, type } = e.target;

    // Skip validation for certain fields
    if (!name || name.includes('proUsed') || name.includes('textreview')) {
      return;
    }

    const validationResult = inputValidator(name, value, checked);

    // Add visual feedback for invalid fields
    if (validationResult.error) {
      e.target.classList.add('input-error');
    } else {
      e.target.classList.remove('input-error');
    }
  };

  // Render loading component while fetching data
  if (loading) {
    return <LoadingComponent />;
  }

  // Extract customer data for form
  const {
    internalState: orderID,
    externalState: externalOrderID,
    emailState: emailCustomer,
  } = decodedState;

  return (
    <Container className="font-family">
      <Row>
        <Col xl={12} className="testimonial_container">
          <form onSubmit={handleSubmit} onChange={handleFieldValidation}>
            <div className="container_components">
              <TestiHeader orderID={orderID} externalOrderID={externalOrderID} data={orderData} />
              <input
                type="hidden"
                name="customerName"
                id="customerName"
                value={orderData.data['shipping_names'] || ''}
              />
              <input
                type="hidden"
                name="customerEmail"
                id="customerEmail"
                value={emailCustomer || ''}
              />
            </div>
            <div className="container_components">
              <TestiEvent data={orderData} />
            </div>
            <div className="container_components">
              <TestiPhotos orderID={orderID} data={orderData} />
            </div>
            <div className="container_components">
              <TestiOrders orderID={orderID} data={orderData} />
            </div>
            <div className="container_components">
              <TestiVendor />
            </div>
            <div className="container_components">
              <TermsAndConditions />
            </div>

            <div className="submitButton">
              <button
                className="we-testimonial-submit ff-button--primary"
                type="submit"
                name="submitBtn"
                id="submitBtn"
                disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"></span>
                    Submitting...
                  </>
                ) : (
                  'Submit Your Entry'
                )}
              </button>
            </div>
          </form>
        </Col>
      </Row>

      <Modal
        show={modalState.show}
        onClose={handleModalClose}
        variant="alert"
        title={modalState.headMsg}
        body={modalState.bodyMsg}
      />

      <Modal
        show={successModal}
        onClose={() => setSuccessModal(false)}
        variant="success"
        title="Thank you!"
        body={
          <h4>
            <strong>Your Story has been Submitted!!</strong>
          </h4>
        }
        actionText="Continue Shopping"
        actionUrl="/"
        imageSrc="https://site-files-new.s3.amazonaws.com/FiftyFlowers/react/testimonials/Static+Media+Files/success.png"
        imageAlt="Wedding Success Flowers"
      />
    </Container>
  );
};

export default TestimonialSubmit;
